import type { Type } from '@angular/core';

// The one rule deciding whether a new popover request is "the same popover" as one already
// open. PopoverController.present and PopoverHostComponent.createPopover both use it, so the
// two places cannot drift apart.
//
// Same component type and same target used to be the whole test. That is right for a popover
// anchored to a real element: opening it twice from one button is a double tap. It is wrong
// for alerts, which are all AlertPopover and all target document.body, so every pair of
// alerts matched, and a second, different question was answered with the first one's answer
// (df-93a444035272). `dedupeKey` closes that gap: a popover that sets it is only "the same"
// as one with an equal key. Popovers that set no key compare equal on it, so they behave
// exactly as before.

export interface PopoverIdentity {
  componentRef: { componentType: Type<any> };
  opts: { targetFromElement?: HTMLElement; dedupeKey?: string };
}

export interface PopoverRequest {
  targetFromElement?: HTMLElement;
  dedupeKey?: string;
}

// same component, same target: the two would sit on top of each other
function sharesSlot(popover: PopoverIdentity, component: Type<any>, request: PopoverRequest): boolean {
  return popover.componentRef.componentType === component && popover.opts.targetFromElement === request?.targetFromElement;
}

/** The open popover IS the requested one, so the request should reuse it and its answer. */
export function isSamePopover(popover: PopoverIdentity, component: Type<any>, request: PopoverRequest): boolean {
  return sharesSlot(popover, component, request) && popover.opts.dedupeKey === request?.dedupeKey;
}

/**
 * The open popover occupies the same slot but is a DIFFERENT one (for alerts: a different
 * question). The request must wait for it to close rather than reuse it or replace it.
 */
export function isCompetingPopover(popover: PopoverIdentity, component: Type<any>, request: PopoverRequest): boolean {
  return sharesSlot(popover, component, request) && popover.opts.dedupeKey !== request?.dedupeKey;
}
